import { Link } from "react-router-dom";
import signalLogoDark from "@/assets/signal-logo-on-dark.svg";
import signalLogoLight from "@/assets/signal-logo-on-light.svg";
import signalMark from "@/assets/signal-mark.svg";

// The "signal" brand lockup (mark + wordmark), shared between the headers, the
// splash, the auth card and the portal sidebar so the branding lives in one
// place (DRY). `size` picks the header (sm) vs the larger hero (lg). The link's
// aria-label carries the full accessible name (the images are decorative,
// alt="").
//
// Every surface it sits on follows the theme (BRK-S249), so it carries both
// official lockups and CSS shows the one that suits the scheme: Primary-Black
// (indigo mark / Ink wordmark) by day, Primary-White (indigo mark / white
// wordmark) by night (BRK-S245). The brand book forbids recolouring the
// artwork, so swapping it is the only honest fix. Getting the pair backwards
// leaves the wordmark invisible, so Brand.test.tsx pins it.
//
// `layout` (BRK-S258): `inline` puts "Broker Portal" beside the lockup (the
// headers, the splash); `logo` is the lockup alone, for the sidebar's top
// block (Nav dropped the label there); `mark` is the indigo mark alone, which
// reads on both themes, for the collapsed rail. The link's name keeps "Broker
// Portal" in all three.
const HOME_NAME = "Signal Specialty Broker Portal — home";

export function Brand({
  size = "sm",
  compact = false,
  layout = "inline",
}: {
  size?: "sm" | "lg";
  /** Drops the "Broker Portal" label below sm, for the portal header, which
   *  has no room for it on a phone (BRK-S248). The link's name keeps it. */
  compact?: boolean;
  layout?: "inline" | "logo" | "mark";
}) {
  if (layout === "mark") {
    return (
      <Link
        to="/"
        aria-label={HOME_NAME}
        className="inline-flex items-center justify-center rounded-md [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
      >
        <img src={signalMark} alt="" className="h-8 w-8" />
      </Link>
    );
  }

  const logoClass = size === "lg" ? "h-10" : "h-[1.8rem]";

  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2.5 text-brk-text no-underline [@media(pointer:coarse)]:min-h-11"
      aria-label={HOME_NAME}
    >
      <img
        src={signalLogoLight}
        alt=""
        className={`${logoClass} w-auto dark:hidden`}
      />
      <img
        src={signalLogoDark}
        alt=""
        className={`hidden ${logoClass} w-auto dark:block`}
      />
      {layout === "inline" && (
        <span
          className={`border-l border-brk-border pl-2.5 text-xs font-medium uppercase tracking-wide text-brk-dim sm:text-sm ${
            compact ? "hidden sm:block" : ""
          }`}
        >
          Broker Portal
        </span>
      )}
    </Link>
  );
}
